import { useEffect, useState } from 'react'
import { useAuth } from 'react-oidc-context'
import { useLocation, useNavigate } from 'react-router-dom'

import { buildReturnUrl, type LocationFrom } from '@/lib/auth/return-url'
import { useAuthStore } from '@/stores/auth-store'

const BACKGROUND_STYLE = {
  backgroundImage: 'url(/images/login-background.jpg)',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
} as const

function LoginLoadingView() {
  return (
    <div
      className="min-h-screen flex items-center justify-center relative"
      style={BACKGROUND_STYLE}
    >
      <div className="absolute inset-0 bg-black/50" />
      <div className="relative text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 mb-4">
          <div className="w-16 h-16 border-4 border-white/20 border-t-white rounded-full animate-spin" />
        </div>
        <p className="text-sm text-white">Loading...</p>
      </div>
    </div>
  )
}

function LoginCard({ error, onSignIn }: { error: string | null; onSignIn: () => void }) {
  return (
    <div className="relative max-w-md w-full">
      <div className="bg-gray-800 rounded-lg shadow-xl p-8">
        <div className="flex justify-center mb-6">
          <img
            src="/images/CloudSent-POS-logo-white-sm.png"
            alt="CloudSent POS"
            className="h-20 w-auto shrink-0 object-contain"
          />
        </div>

        <p className="text-center text-gray-300 mb-8">CloudSent Admin & Reports</p>

        {error && (
          <div className="mb-6 rounded-md bg-red-900/20 p-4">
            <p className="text-sm text-red-200">{error}</p>
          </div>
        )}

        <button
          onClick={onSignIn}
          className="w-full flex items-center justify-center gap-3 py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          Login with CloudSent SSO
        </button>

        <p className="mt-6 text-xs text-center text-gray-400">
          Secure single sign-on authentication
        </p>
      </div>
    </div>
  )
}

export function LoginPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const auth = useAuth()
  const { isAuthenticated, needsSystemSelection } = useAuthStore()
  const [error, setError] = useState<string | null>(null)

  const from = (location.state as { from?: LocationFrom } | undefined)?.from
  const returnUrl = buildReturnUrl(from)

  useEffect(() => {
    if (needsSystemSelection) {
      navigate('/select-system', { replace: true, state: { returnUrl } })
      return
    }

    if (isAuthenticated) {
      navigate(returnUrl, { replace: true })
    }
  }, [isAuthenticated, needsSystemSelection, navigate, returnUrl])

  useEffect(() => {
    if (auth.error) {
      setError(auth.error.message || 'Authentication failed')
    }
  }, [auth.error])

  const handleSignIn = async () => {
    try {
      setError(null)
      await auth.signinRedirect({ state: { returnUrl } })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to initiate sign in')
    }
  }

  if (auth.isLoading) {
    return <LoginLoadingView />
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 relative"
      style={BACKGROUND_STYLE}
    >
      <div className="absolute inset-0 bg-black/50" />
      <LoginCard error={error} onSignIn={handleSignIn} />
    </div>
  )
}
